import { Button, Heading, Input, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { type AssetType, type Stored } from "@overmap-ai/common"
import { selectAssetTypesOfOrganization } from "@overmap-ai/core"
import { useTextFilter } from "@overmap-ai/hooks"
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { useAssetTypeTable, useAssetTypeTableColumns } from "../../components/Tables/AssetTypeTable"
import { Table } from "../../components/Tables/Table"
import { assetTypeFilterCallback } from "../../filtering/utils"
import { useChangeEventHandler } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/asset-types/")

export const AssetTypeSettingsView = memo(() => {
	const { organizationId } = routeApi.useParams()

	const assetTypes = useShallowEqaulSelector(selectAssetTypesOfOrganization(organizationId))
	const navigate = useNavigate()

	const handleRowClick = useCallback(
		(assetType: Stored<AssetType>) => {
			void navigate({
				from: "/$organizationId/settings/asset-types/",
				to: "/$organizationId/settings/asset-types/$assetTypeId",
				params: (prev) => ({ ...prev, assetTypeId: assetType.uuid }),
			})
		},
		[navigate],
	)

	const [filteredAssetTypes, filterValue, setFilterValue] = useTextFilter(assetTypes, assetTypeFilterCallback)

	const columns = useAssetTypeTableColumns()

	const table = useAssetTypeTable({
		data: filteredAssetTypes,
		columns: columns,
		enableHiding: false,
	})

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">Asset types</Heading>
					<Text size="sm" accentColor="base">
						Manage the asset types in your organization.
					</Text>
				</div>

				<Separator size="full" />

				<div className="flex min-h-0 grow flex-col gap-2">
					<div className="flex w-full items-center justify-between gap-2">
						<Input.Root variant="outline">
							<Input.Slot>
								<LuIcon icon={modelIconMapping.search} />
							</Input.Slot>
							<Input.Field
								placeholder="Search"
								value={filterValue}
								onChange={useChangeEventHandler(setFilterValue)}
							/>
						</Input.Root>

						<Link
							from="/$organizationId/settings/asset-types/"
							to="/$organizationId/settings/asset-types/create"
						>
							<Button size="sm" accentColor="primary" variant="surface">
								<LuIcon icon={modelIconMapping.add} /> Create
							</Button>
						</Link>
					</div>

					<div className="min-h-0 w-full grow overflow-auto">
						<Table
							className="w-full"
							size="sm"
							variant="surface"
							table={{ ...table }}
							onRowClick={handleRowClick}
							interactive
							emptyText="No asset types."
						/>
					</div>
				</div>
			</section>
		</ViewContainer>
	)
})
AssetTypeSettingsView.displayName = "AssetTypeSettingsView"
